import type { Router } from 'vue-router';

import { dataFrom, OpenApiError } from '@zyno-io/openapi-client-codegen';
import { UserError } from '@zyno-io/vue-foundation';
import { computed, ref, watch } from 'vue';

import { GlobalState, type ITenant } from '@/global';
import { MobileBridge } from '@/modules/mobile/helper/bridge';
import {
    AuthIdentityApi,
    AuthLoginApi,
    AuthPermissionsApi,
    type IIdentityResponse,
    type ILoginMethodsClaim,
    type ILoginResponse,
    type ITotpChallengeResponse,
    type IWorkstationListItem
} from '@/openapi-clients-generated/auth';
import { useStore } from '@/store';

import { DataCache } from '../data/cache';
import { decodeJwtPayload } from '../helpers/jwt.helpers';
import { JwtRenewalCoordinator, type JwtRenewalResult } from './jwt-renewal-coordinator';
import { Logger } from './logger';

const logger = Logger.createScoped('AuthService');
const WorkstationIdleTimeoutStorageKey = 'zs:auth:wsIdleTimeoutSeconds';
const TabJwtKey = 'zs:auth:tabJwt';
const TabJwtExpKey = 'zs:auth:tabJwtExp';
const TabInitializedKey = 'zs:auth:tabInitialized';
const TabRememberMeKey = 'zs:auth:tabRememberMeExpiresAt';

/** The subset of session-JWT claims the SPA reads. */
interface ISessionJwtClaims {
    sub?: string;
    loginMethods?: ILoginMethodsClaim;
    /** `<sessionId>:<renewalIndex>` — see `sessionId` below. */
    jti?: string;
}

const NoLoginMethods: ILoginMethodsClaim = {
    password: { configured: false, usable: false, setupAllowed: false },
    passkey: { configured: false, usable: false, setupAllowed: false },
    totp: { configured: false, usable: false, setupAllowed: false }
};

class AuthService {
    authStorage: Storage = sessionStorage;
    logoutCause: string | undefined;
    renewPromise: Promise<void> | null = null;
    private unauthorizedRecoveryPromise: Promise<boolean> | null = null;
    private lastUnauthorizedRecovery: { jwt: string; validUntil: number } | null = null;
    private _workstationIdleTimeoutSeconds: number | null | undefined;

    userId = ref<string | null>(sessionStorage.getItem('zs:auth:userId') ?? localStorage.getItem('zs:auth:lastUserId'));
    tenantId = ref<string | null>(sessionStorage.getItem('zs:auth:tenantId') ?? localStorage.getItem('zs:auth:lastTenantId'));
    impersonatorUserId = ref<string | null>(
        sessionStorage.getItem('zs:auth:impersonatorUserId') ?? localStorage.getItem('zs:auth:lastImpersonatorUserId')
    );
    impersonatorTenantId = ref<string | null>(
        sessionStorage.getItem('zs:auth:impersonatorTenantId') ?? localStorage.getItem('zs:auth:lastImpersonatorTenantId')
    );
    rememberMeExpiresAt = ref<number | null>(
        parseInt(sessionStorage.getItem(TabRememberMeKey) ?? localStorage.getItem('zs:auth:rememberMeExpiresAt') ?? '') || null
    );

    /** Claims of the JWT currently in storage, re-read whenever that token is replaced. */
    private jwtClaims = ref<ISessionJwtClaims | null>(null);

    /**
     * Which local sign-in factors this session may use or set up.
     *
     * The JWT claim wins over `/identity` because the server authorizes the factor
     * endpoints from that same claim — while masquerading, `/identity` describes the
     * impersonated account but the JWT still describes the account the API will act on.
     * `/identity` covers sessions whose token predates the claim.
     */
    loginMethods = computed<ILoginMethodsClaim>(() => this.jwtClaims.value?.loginMethods ?? GlobalState.user.value?.loginMethods ?? NoLoginMethods);

    /**
     * Whether either source actually reported the factors, as opposed to `loginMethods`
     * falling back to its deny-everything default. Screens that explain why a factor is
     * unavailable need the difference: "the API says no" and "the API said nothing" look
     * identical in the value itself, but only one of them is a misconfiguration.
     */
    hasLoginMethods = computed(() => !!(this.jwtClaims.value?.loginMethods ?? GlobalState.user.value?.loginMethods));

    /**
     * Id of the auth session this JWT belongs to. The server mints `jti` as
     * `<sessionId>:<renewalIndex>` and keeps the session id across renewals, so it is stable for the
     * life of the session. null means *unknown* — not "no session" — for a token minted before the
     * claim existed; callers must not read that as a non-match.
     */
    sessionId = computed(() => this.jwtClaims.value?.jti?.split(':')[0] || null);

    constructor() {
        // localStorage is only the original sign-in credential used to open a new tab. Once
        // initialized, a tab owns its token and a failed renewal cannot clear a sibling's token.
        if (!sessionStorage.getItem(TabInitializedKey)) {
            const jwt = localStorage.getItem('zs:auth:sessionJwt');
            if (jwt) {
                sessionStorage.setItem(TabJwtKey, jwt);
                sessionStorage.setItem(TabJwtExpKey, localStorage.getItem('zs:auth:sessionJwtExp') ?? '0');
                sessionStorage.setItem(TabInitializedKey, '1');
            }
        }
        this.refreshJwtClaims();
        this.setupRefWatchers();
        if (this.activeJwt) this.validateBrowserSession();
        window.addEventListener('storage', event => {
            if (event.storageArea !== localStorage || event.key !== 'zs:auth:sessionJwt' || event.newValue !== null) return;
            const oldClaims = decodeJwtPayload<ISessionJwtClaims>(event.oldValue);
            if (oldClaims?.jti?.split(':')[0] === this.sessionId.value) this.clearStoredJwt();
        });
    }

    private refreshJwtClaims() {
        this.jwtClaims.value = decodeJwtPayload<ISessionJwtClaims>(this.activeJwt);
    }

    /**
     * Redeem a transient signup credential from the fragment before any ordinary session restoration.
     * Fragments never reach the server, and the credential is removed from browser history before the
     * redemption request so no subsequent route or telemetry path can observe it.
     */
    async consumeOneTimeLoginFromUrl(): Promise<boolean> {
        const match = /^#auth:([A-Za-z0-9_-]{32,128})(;.*)?$/.exec(location.hash);
        if (!match) return false;

        const token = match[1]!;
        const remainingHash = match[2] ? `#${match[2].slice(1)}` : '';
        history.replaceState(null, '', `${location.pathname}${location.search}${remainingHash}`);
        try {
            const { data } = await AuthLoginApi.postLoginOneTimeLogin<true>({
                body: { browserId: GlobalState.browserId, token },
                credentials: 'include'
            });
            if (isTotpChallenge(data)) {
                sessionStorage.setItem('zs:auth:totpCeremonyId', data.ceremonyId);
            } else {
                await this.processLoginResponse(data);
            }
        } catch (err) {
            // Do not include the URL or token in telemetry/logging. The normal login screen remains available.
            logger.warn('One-time login handoff could not be redeemed', { err });
        }
        return true;
    }

    // COMPUTED/WATCHED REFS

    private setupRefWatchers() {
        watch(this.userId, newUserId => {
            this.persistValueToStorage(sessionStorage, 'zs:auth:userId', newUserId);
            if (newUserId) this.setupAuthedRefWatchers();
            this.persistLastAuthDetails();
        });
        watch(this.tenantId, newTenantId => {
            this.persistValueToStorage(sessionStorage, 'zs:auth:tenantId', newTenantId);
            this.persistLastAuthDetails();
        });
        watch(this.impersonatorUserId, newImpersonatorUserId => {
            this.persistValueToStorage(sessionStorage, 'zs:auth:impersonatorUserId', newImpersonatorUserId);
            this.persistLastAuthDetails();
        });
        watch(this.impersonatorTenantId, newImpersonatorTenantId => {
            this.persistValueToStorage(sessionStorage, 'zs:auth:impersonatorTenantId', newImpersonatorTenantId);
            this.persistLastAuthDetails();
        });
        watch(this.rememberMeExpiresAt, newExpiresAt =>
            this.persistValueToStorage(sessionStorage, TabRememberMeKey, newExpiresAt ? String(newExpiresAt) : null)
        );

        watch(GlobalState.user, () => this.setupAuthedRefWatchers());
        window.addEventListener('focus', () => this.persistLastAuthDetails());
    }

    private hasSetupAuthedRefWatchers = false;
    private setupAuthedRefWatchers() {
        if (this.hasSetupAuthedRefWatchers) return;
        this.hasSetupAuthedRefWatchers = true;
        watch(
            [DataCache.getLazyRef('talkAccess'), GlobalState.permissions],
            ([talkAccess, permissions]) => {
                if (!permissions) return;
                // Tenant admins keep whatever the permissions endpoint gave us: it omits `talk`
                // entirely when the tenant isn't provisioned for Talk. Everyone else needs at
                // least one Talk access row, since admin-less users get no blanket grant.
                if (GlobalState.tenant.value?.isAdmin) return;
                permissions.talk = talkAccess?.length ? {} : undefined;
            },
            { immediate: true }
        );
    }

    private persistValueToStorage(storage: Storage, key: string, value: string | null) {
        if (value === null) {
            storage.removeItem(key);
        } else {
            storage.setItem(key, value);
        }
    }

    private persistLastAuthDetails() {
        if (GlobalState.workstation.value) return;
        this.persistValueToStorage(localStorage, 'zs:auth:lastUserId', this.userId.value);
        this.persistValueToStorage(localStorage, 'zs:auth:lastTenantId', this.tenantId.value);
        this.persistValueToStorage(localStorage, 'zs:auth:lastImpersonatorUserId', this.impersonatorUserId.value);
        this.persistValueToStorage(localStorage, 'zs:auth:lastImpersonatorTenantId', this.impersonatorTenantId.value);
    }

    // GETTERS

    get logoutCauseOnce() {
        const cause = this.logoutCause;
        this.logoutCause = undefined;
        return cause;
    }

    // INIT

    get activeJwt() {
        return this.authStorage.getItem(GlobalState.workstation.value ? 'zs:auth:sessionJwt' : TabJwtKey);
    }

    get activeJwtExp() {
        const exp = Number.parseInt(this.authStorage.getItem(GlobalState.workstation.value ? 'zs:auth:sessionJwtExp' : TabJwtExpKey) ?? '');
        return Number.isFinite(exp) ? exp : 0;
    }

    // The workstation's configured idle timeout (seconds), delivered with each login/renewal.
    // null (or unset) means no idle timeout. Persisted alongside the JWT so it survives reloads
    // until the next renewal refreshes it.
    get workstationIdleTimeoutSeconds(): number | null {
        if (this._workstationIdleTimeoutSeconds !== undefined) return this._workstationIdleTimeoutSeconds;
        return this.readStoredWorkstationIdleTimeout();
    }

    // STATE SETUP

    setWorkstation(workstation: Pick<IWorkstationListItem, 'id'>) {
        this.authStorage = sessionStorage;
        this._workstationIdleTimeoutSeconds = this.readStoredWorkstationIdleTimeout();
        GlobalState.workstation.value = workstation;
        this.refreshJwtClaims();
        logger.info(`Set workstation to ${workstation.id}`);
    }

    setUser(user: IIdentityResponse) {
        this.userId.value = user.id;
        GlobalState.user.value = user;
        logger.info(`Set user to ${user.id}`);
    }

    setTenant(tenant: Pick<ITenant, 'id' | 'name' | 'isAdmin' | 'mspId'>) {
        this.tenantId.value = tenant.id;
        GlobalState.tenant.value = tenant;
        logger.info(`Set tenant to ${tenant.id}`);
    }

    setDefaultTenant() {
        const user = GlobalState.user.value!;
        let tenant = this.tenantId.value && user.tenants.find(t => t.id === this.tenantId.value);
        if (!tenant && this.impersonatorTenantId.value) {
            this.clearImpersonatedTenant();
            tenant = user.tenants.find(t => t.id === this.tenantId.value);
        }
        if (!tenant) {
            tenant = user.tenants.find(t => t.isFavorite);
        }
        if (!tenant) {
            tenant = user.tenants[0];
        }
        if (!tenant) {
            throw new UserError('You do not have access to any tenants. Please contact your administrator.');
        }
        this.setTenant(tenant!);
    }

    // IMPERSONATION

    setImpersonatedUserId(id: string) {
        if (this.impersonatorUserId.value) throw new Error('Cannot set impersonator user when one is already set');
        this.impersonatorUserId.value = this.userId.value;
        this.userId.value = id;
        logger.info(`Set impersonated user ID to ${id}`);
    }

    clearImpersonatedUser() {
        if (!this.impersonatorUserId.value) throw new Error('Cannot clear impersonator user when one is not set');
        this.userId.value = this.impersonatorUserId.value;
        this.impersonatorUserId.value = null;
        logger.info('Cleared impersonator user ID');
    }

    setImpersonatedTenantId(id: string) {
        if (this.impersonatorTenantId.value) throw new Error('Cannot set impersonator tenant when one is already set');
        this.impersonatorTenantId.value = this.tenantId.value;
        this.tenantId.value = id;
        logger.info(`Set impersonated tenant ID to ${id}`);
    }

    clearImpersonatedTenant() {
        if (!this.impersonatorTenantId.value) throw new Error('Cannot clear impersonator tenant when one is not set');
        this.tenantId.value = this.impersonatorTenantId.value;
        this.impersonatorTenantId.value = null;
        logger.info('Cleared impersonator tenant ID');
    }

    // SESSION MANAGEMENT

    async processLoginResponse(response: ILoginResponse & { idleTimeoutSeconds?: number | null }) {
        this.storeJwtFromResponse(response);
        this.storeWorkstationIdleTimeout(response.idleTimeoutSeconds);
        this.rememberMeExpiresAt.value = response.rememberMeExpiresAt ?? null;
        if (!GlobalState.workstation.value && !MobileBridge.isWithinZynoSuiteMobile) {
            localStorage.setItem('zs:auth:sessionJwt', response.jwt);
            localStorage.setItem('zs:auth:sessionJwtExp', String(response.expiresAt));
            this.persistValueToStorage(
                localStorage,
                'zs:auth:rememberMeExpiresAt',
                response.rememberMeExpiresAt ? String(response.rememberMeExpiresAt) : null
            );
        }

        // if we were told that we have an impersonator tenant ID, set it
        if (response.impersonatorTenantId) {
            this.impersonatorTenantId.value = response.impersonatorTenantId;
        }

        // if we're impersonating, reload the identity with masquerade headers
        if (this.impersonatorTenantId.value || this.impersonatorUserId.value) {
            response.identity = dataFrom(await AuthIdentityApi.getIdentityGetIdentity());
        }

        try {
            this.setUser(response.identity);
            this.setDefaultTenant();
            await this.reloadPermissions();
        } catch (err) {
            this.clearStoredJwt();
            throw err;
        }
    }

    private get jwtRenewalScope() {
        return `${this.browserSessionScope}:tab:${JwtRenewalCoordinator.ownerId}`;
    }

    get browserSessionScope() {
        return `browser:${GlobalState.browserId}:session:${this.sessionId.value ?? 'legacy'}`;
    }

    async renewJwt(expectedExpiresAt = this.activeJwtExp) {
        if (!this.renewPromise) {
            const renewPromise = GlobalState.workstation.value
                ? this.renewJwtForCurrentWindow(expectedExpiresAt)
                : JwtRenewalCoordinator.renew({
                      scope: this.jwtRenewalScope,
                      lockScope: this.browserSessionScope,
                      shouldRenew: () => this.shouldRenewJwt(expectedExpiresAt),
                      performRenewal: () => this.renewJwtInternal()
                  });

            this.renewPromise = renewPromise.finally(() => {
                this.renewPromise = null;
            });
        }
        return this.renewPromise;
    }

    /**
     * Recover once from a JWT that an API has rejected. Calls are deduplicated
     * with scheduled/cross-tab renewal, and a freshly renewed token that is
     * immediately rejected fails closed instead of starting a renewal loop.
     */
    async recoverUnauthorizedJwt(rejectedJwt = this.activeJwt, rejectedExpiresAt = this.activeJwtExp): Promise<boolean> {
        if (this.unauthorizedRecoveryPromise) return this.unauthorizedRecoveryPromise;

        let recovery!: Promise<boolean>;
        recovery = this.recoverUnauthorizedJwtInternal(rejectedJwt, rejectedExpiresAt).finally(() => {
            if (this.unauthorizedRecoveryPromise === recovery) this.unauthorizedRecoveryPromise = null;
        });
        this.unauthorizedRecoveryPromise = recovery;
        return recovery;
    }

    private async recoverUnauthorizedJwtInternal(rejectedJwt: string | null, rejectedExpiresAt: number): Promise<boolean> {
        const currentJwt = this.activeJwt;
        if (!currentJwt) return false;

        // Another request/tab may already have replaced the rejected token.
        if (rejectedJwt && currentJwt !== rejectedJwt) return true;
        const recoveryTargetJwt = rejectedJwt ?? currentJwt;

        if (this.lastUnauthorizedRecovery?.jwt === recoveryTargetJwt && this.lastUnauthorizedRecovery.validUntil > Date.now()) {
            logger.info('A JWT renewed after a 401 was immediately rejected; clearing session');
            this.clearStoredJwt('Your session expired. Please sign in again.');
            return false;
        }

        try {
            await this.renewJwt(rejectedExpiresAt);
        } catch (err) {
            logger.warn('JWT recovery after 401 failed', err);
        }

        const renewedJwt = this.activeJwt;
        const renewedExpiresAt = this.activeJwtExp;
        if (renewedJwt && (renewedJwt !== recoveryTargetJwt || renewedExpiresAt > rejectedExpiresAt)) {
            this.lastUnauthorizedRecovery = {
                jwt: renewedJwt,
                validUntil: Date.now() + 60_000
            };
            logger.info('Recovered JWT after API returned 401');
            return true;
        }

        if (this.activeJwt) this.clearStoredJwt('Your session expired. Please sign in again.');
        return false;
    }

    private shouldRenewJwt(expectedExpiresAt: number) {
        if (!this.activeJwt) return false;
        return this.activeJwtExp <= expectedExpiresAt;
    }

    private async renewJwtForCurrentWindow(expectedExpiresAt: number) {
        if (!this.shouldRenewJwt(expectedExpiresAt)) return;
        await this.renewJwtInternal();
    }

    private async renewJwtInternal(): Promise<JwtRenewalResult> {
        if (!this.validateBrowserSession()) return { status: 'cleared' };
        // Once one tab has rotated the browser cookie, siblings can copy its original-account
        // credential into their own storage without rotating that cookie again.
        if (this.adoptNewerBrowserSeed()) return { status: 'renewed' };
        const previousJwt = this.activeJwt;
        try {
            // A factor mutation while impersonating can reissue a JWT for the target account.
            // Renew the original session with its own credential, never that target credential.
            const seedJwt = localStorage.getItem('zs:auth:sessionJwt');
            const seedClaims = decodeJwtPayload<ISessionJwtClaims>(seedJwt);
            const renewalJwt = !GlobalState.workstation.value && seedClaims?.jti?.split(':')[0] === this.sessionId.value ? seedJwt : previousJwt;
            const result = await AuthLoginApi.postLoginRenew({
                body: {
                    browserId: GlobalState.workstation.value?.id ?? GlobalState.browserId
                },
                headers: renewalJwt ? { Authorization: `Bearer ${renewalJwt}` } : undefined,
                credentials: 'include'
            });
            const response = dataFrom(result);
            if (this.activeJwt !== previousJwt) return { status: 'failed' };
            if (!this.validateBrowserSession()) return { status: 'cleared' };
            this.storeJwtFromResponse(response);
            this.storeWorkstationIdleTimeout(response.idleTimeoutSeconds);
            return { status: 'renewed' };
        } catch (err) {
            // if we already get a 401, it's too late
            if (err instanceof OpenApiError && err.response?.status === 401) {
                logger.info('JWT renewal failed with 401');
                if (this.activeJwt === previousJwt) {
                    if (this.adoptNewerBrowserSeed()) return { status: 'renewed' };
                    this.clearStoredJwt();
                }
                return { status: 'cleared' };
            }
            logger.warn('Failed to renew JWT', err);
            return { status: 'failed' };
        }
    }

    /** The persistent seed is authoritative even if a suspended tab missed a storage event. */
    validateBrowserSession() {
        if (GlobalState.workstation.value || MobileBridge.isWithinZynoSuiteMobile) return true;
        if (!this.activeJwt) return false;
        const current = decodeJwtPayload<ISessionJwtClaims>(this.activeJwt);
        const seed = decodeJwtPayload<ISessionJwtClaims>(localStorage.getItem('zs:auth:sessionJwt'));
        if (current?.sub && seed?.sub === current.sub && current.jti && seed.jti?.split(':')[0] === current.jti.split(':')[0]) return true;
        this.clearStoredJwt('Your session expired. Please sign in again.');
        return false;
    }

    private adoptNewerBrowserSeed() {
        if (GlobalState.workstation.value || MobileBridge.isWithinZynoSuiteMobile) return false;
        const jwt = localStorage.getItem('zs:auth:sessionJwt');
        const expiresAt = Number(localStorage.getItem('zs:auth:sessionJwtExp'));
        if (!jwt || jwt === this.activeJwt || !Number.isFinite(expiresAt) || expiresAt <= Date.now() || expiresAt < this.activeJwtExp) return false;
        if (!this.validateBrowserSession()) return false;
        this.storeJwtFromResponse({ jwt, expiresAt });
        return true;
    }

    storeJwtFromResponse(response: Pick<ILoginResponse, 'jwt' | 'expiresAt'>) {
        const seedClaims = decodeJwtPayload<ISessionJwtClaims>(localStorage.getItem('zs:auth:sessionJwt'));
        const claims = decodeJwtPayload<ISessionJwtClaims>(response.jwt);
        if (
            !GlobalState.workstation.value &&
            seedClaims?.sub &&
            claims?.sub &&
            seedClaims.jti?.split(':')[0] === claims.jti?.split(':')[0] &&
            seedClaims.sub !== claims.sub
        ) {
            // Factor changes can return a JWT for the masqueraded account using the original
            // session id. Keep authenticating with the original credential and tab headers.
            logger.info('Retained original session credential after impersonated account reissue');
            return;
        }
        this.authStorage.setItem(GlobalState.workstation.value ? 'zs:auth:sessionJwt' : TabJwtKey, response.jwt);
        this.authStorage.setItem(GlobalState.workstation.value ? 'zs:auth:sessionJwtExp' : TabJwtExpKey, String(response.expiresAt));
        sessionStorage.setItem(TabInitializedKey, '1');
        // Keep the new-tab seed fresh only for the same original account and session. A JWT
        // reissued for an impersonated account must never become another tab's sign-in credential.
        if (!GlobalState.workstation.value) {
            if (seedClaims?.sub && seedClaims.sub === claims?.sub && seedClaims.jti?.split(':')[0] === claims.jti?.split(':')[0]) {
                localStorage.setItem('zs:auth:sessionJwt', response.jwt);
                localStorage.setItem('zs:auth:sessionJwtExp', String(response.expiresAt));
            }
        }
        this.refreshJwtClaims();
        logger.info('Stored JWT');
    }

    setWorkstationIdleTimeout(seconds: number | null) {
        this.storeWorkstationIdleTimeout(seconds);
    }

    private readStoredWorkstationIdleTimeout(): number | null {
        const raw = this.authStorage.getItem(WorkstationIdleTimeoutStorageKey);
        if (raw === null) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private storeWorkstationIdleTimeout(seconds: number | null | undefined) {
        // Only workstation sessions carry/observe this value.
        if (!GlobalState.workstation.value) return;
        // undefined → response omitted it, so keep the existing value; null → no idle timeout.
        if (seconds === undefined) return;
        this._workstationIdleTimeoutSeconds = seconds;
        this.persistValueToStorage(this.authStorage, WorkstationIdleTimeoutStorageKey, seconds === null ? null : String(seconds));
    }

    clearStoredJwt(cause?: string) {
        this.lastUnauthorizedRecovery = null;
        GlobalState.user.value = null;

        if (!GlobalState.workstation.value) {
            GlobalState.tenant.value = null;
        }

        this.rememberMeExpiresAt.value = null;
        this.authStorage.removeItem(GlobalState.workstation.value ? 'zs:auth:sessionJwt' : TabJwtKey);
        this.authStorage.removeItem(GlobalState.workstation.value ? 'zs:auth:sessionJwtExp' : TabJwtExpKey);
        sessionStorage.setItem(TabInitializedKey, '1');
        sessionStorage.removeItem(TabRememberMeKey);
        this.jwtClaims.value = null;
        this.authStorage.removeItem(WorkstationIdleTimeoutStorageKey);
        this._workstationIdleTimeoutSeconds = undefined;

        if (this.impersonatorTenantId.value) {
            if (GlobalState.workstation.value) {
                // on ZynoWorkstation, just clear the impersonator without swapping tenantId,
                // so the workstation's tenant is preserved for the next login
                this.impersonatorTenantId.value = null;
            } else {
                this.clearImpersonatedTenant();
            }
        }

        if (cause) {
            this.logoutCause = cause;
        }

        logger.info('Cleared user session', { cause });
    }

    /** Explicit logout and a genuinely idle browser session retire the new-tab seed too. */
    clearBrowserSession(cause?: string) {
        if (!GlobalState.workstation.value) {
            localStorage.removeItem(`zs:auth:lastActivity:${this.browserSessionScope}`);
            const seedClaims = decodeJwtPayload<ISessionJwtClaims>(localStorage.getItem('zs:auth:sessionJwt'));
            if (seedClaims?.jti?.split(':')[0] === this.sessionId.value) {
                localStorage.removeItem('zs:auth:sessionJwt');
                localStorage.removeItem('zs:auth:sessionJwtExp');
                localStorage.removeItem('zs:auth:rememberMeExpiresAt');
            }
        }
        this.clearStoredJwt(cause);
    }

    // LOADERS

    async loadExistingSessionInitial() {
        if (!this.validateBrowserSession()) return;
        this.loadStoredJwt();
        return this.loadExistingSession();
    }

    async loadExistingSession() {
        if (!this.validateBrowserSession()) return;
        if (!MobileBridge.isWithinZynoSuiteMobile) {
            if (!this.activeJwt) return;
            const activeJwtExp = this.activeJwtExp;
            if (activeJwtExp <= Date.now()) {
                logger.info('Stored JWT has expired. Attempting renewal...');
                await this.renewJwt(activeJwtExp);
                if (!this.activeJwt) return;
                if (this.activeJwtExp <= Date.now()) {
                    logger.info('JWT renewal failed or still expired. Clearing session.');
                    return this.clearStoredJwt();
                }
            }
        }

        const tryLogin = async () => {
            if (MobileBridge.isWithinZynoSuiteMobile) await this.adoptMobileTenant();
            const user = dataFrom(await AuthIdentityApi.getIdentityGetIdentity());
            this.setUser(user);
            this.setDefaultTenant();
            await this.reloadPermissions();
        };

        try {
            await tryLogin();
        } catch (err) {
            if (err instanceof OpenApiError && err.response?.status === 403 && (this.impersonatorUserId.value || this.impersonatorTenantId.value)) {
                logger.info('Impersonation no longer valid. Clearing impersonation and retrying login.');
                if (this.impersonatorUserId.value) this.clearImpersonatedUser();
                if (this.impersonatorTenantId.value) this.clearImpersonatedTenant();
                try {
                    return await tryLogin();
                } catch (err) {
                    logger.error('Login failed after clearing impersonation. Clearing stored JWT.', err);
                    this.clearStoredJwt();
                }
            }

            console.log(err);
            // the 401 from the API middlware will have already cleared the user session before we get here
        }
    }

    /**
     * Inside the ZynoSuite mobile app the native shell owns the tenant: it signs every request with its
     * own `x-zs-tid` / `x-zs-masq-tid`, and remounts this webview when the user switches. Start from its
     * pick, not this webview's stored one, so `Auth.tenantId` names the tenant the data comes from.
     */
    private async adoptMobileTenant() {
        const config = await MobileBridge.configLoaded;
        if (!config?.tenantId) return; // a native build that doesn't report it
        this.tenantId.value = config.masqTenantId ?? config.tenantId;
        this.impersonatorTenantId.value = config.masqTenantId ? config.tenantId : null;
    }

    private loadStoredJwt() {
        if (!this.activeJwt) return;
        // loadExistingSession attempts renewal, using a newer original credential if a
        // sibling already refreshed it. A locally expired token does not prove inactivity.
        if (!this.tenantId.value) {
            logger.info('No tenant ID found in storage. Clearing stored JWT.');
            return this.clearStoredJwt();
        }
        logger.info('Loaded stored JWT');
    }

    async reloadExistingSessionForTenantSwitch(router: Router) {
        await this.loadExistingSession();

        const currentRoute = router.currentRoute.value;
        const matches = currentRoute.fullPath.match(/\/[0-9a-f-]{36}/);
        if (matches) {
            await router.push(currentRoute.fullPath.substring(0, matches.index!));
        }
    }

    async reloadPermissions(clearBeforeReload = true) {
        if (clearBeforeReload) {
            GlobalState.permissions.value = null;
        }
        try {
            GlobalState.permissions.value = dataFrom(await AuthPermissionsApi.getPermissionsGet());
            logger.info('Loaded user permissions');
        } catch {
            useStore().globalError = 'There was an issue loading user permissions. Please check your Internet connection and refresh the page.';
        }
    }
}

function isTotpChallenge(value: ILoginResponse | ITotpChallengeResponse): value is ITotpChallengeResponse {
    return 'next' in value && value.next === 'totp';
}

export const Auth = new AuthService();
